// import { useQnaFeedStore } from '@ninjacart/nc-nk-advisory';
import { IQnACommentResponseItem, IQnAReplyItem } from '@ninjacart/nc-nk-core';
import { FallBackImage, truncateName, useProfileStore } from '@ninjacart/nc-nk-store';
import {
  Avatar,
  Input,
  Paragraph,
  Spinner,
  Stack,
  TextArea,
  View,
  XStack,
  YStack,
  useTimeAgo,
} from '@ninjacart/nc-nk-ui';
import { FlashList } from '@shopify/flash-list';
import { SendHorizontal } from '@tamagui/lucide-icons';
import { t } from 'i18next';
import { RefObject, useState } from 'react';
import { GestureResponderEvent } from 'react-native';

export function ReplyCommentsFlashList({
  reply,
  replyComments,
  addCommentToReply,
  setComment,
  comment,
  textAreaCommentRef,
  handleViewProfileClick,
  useQnaFeedStore,
  index
}: {
  reply: IQnAReplyItem;
  replyComments: IQnACommentResponseItem[];
  addCommentToReply: Function;
  setComment: (string) => void;
  comment: string;
  textAreaCommentRef: RefObject<Input>;
  handleViewProfileClick: Function;
  useQnaFeedStore: any;
  index: number | string;
}) {
  const userProfile = useProfileStore((state) => state.userProfile);
  const authorImage = userProfile?.user?.mediaDetails?.media[0]?.mediaUrl;
  const setCommentInputHidden = useQnaFeedStore((state) => state.setCommentInputHidden);
  const [onEndReachedSpinner, setOnEndReachedSpinner] = useState<boolean>(false);
  const [isReplySubmitting, setIsReplySubmitting] = useState<boolean>(false);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);

  const onCommentInputPress = (event: GestureResponderEvent): void => {
    event.preventDefault();
    setCommentInputHidden(true);
  };

  const onEndReached = async () => {
    try {
      console.log('Comments on end reached callback!!!');
      setOnEndReachedSpinner(true);
      // await initializeQnAList();
    } catch (error) {
      console.error('On End reached All QnA err => ', error);
    } finally {
      setOnEndReachedSpinner(false);
    }
  };

  const renderAnswerComments = ({ item }: { item: IQnACommentResponseItem; index: number }) => {
    return (
      <YStack>
        <XStack marginTop={20}>
          <Avatar
            circular
            size="$3"
            marginTop={10}
            onPress={() => handleViewProfileClick(item?.creatorid.split(':')[0])}
            testID={`kisan_tId_view_profile_click_rply_comments${index}`}
          >
            <Avatar.Image accessibilityLabel="" source={{ uri: FallBackImage }} />
            <Avatar.Fallback delayMs={600} />
          </Avatar>
          <YStack
            padding={10}
            marginLeft={10}
            borderRadius={10}
            flex={1}
            paddingBottom={10}
            backgroundColor={'$commentSectionBackgroundColor'}
          >
            <XStack
              flexDirection="row"
              onPress={() => handleViewProfileClick(item?.creatorid.split(':')[0])}
              testID={`kisan_tId_view_profile_click_rply_comments${index}`}
            >
              <Paragraph
                fontSize={14}
                color={'$commentSectionTextColor'}
                marginBottom={1}
                fontWeight={'600'}
                paddingBottom={0}
              >
                {item?.authorName}
              </Paragraph>
            </XStack>
            <Paragraph fontSize={11} fontWeight={'500'} color={'$commentSectionTextColor'}>
              {item?.timestamp && useTimeAgo(item?.timestamp)}
            </Paragraph>
            <Paragraph
              style={{
                fontSize: 14,
                fontWeight: '500',
                marginTop: 8,
                paddingLeft: 3,
                color: '#A7ABAF',
              }}
            >
              {item?.comment}
            </Paragraph>
          </YStack>
        </XStack>
      </YStack>
    );
  };

  const handleAddComment = async () => {
    console.log("Add comment called!!");
    if (!hasUserLoggedIn()) {
      return;
    }
    const prev = comment;
    if (isReplySubmitting || comment.trim().length === 0) return;
    setIsReplySubmitting(true);
    console.log("Submitting comment!!!");
    try {
      const response = await addCommentToReply(reply);
      setComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      setComment(prev);
    } finally {
      setIsReplySubmitting(false);
    }
  };

  return (
    <>
      <XStack
        paddingTop={12}
        paddingHorizontal={12}
        height={replyComments?.length > 0 ? 'unset' : 0}
      >
        <FlashList
          data={replyComments}
          renderItem={renderAnswerComments}
          keyExtractor={(item, index) => `${item?.id}-${index}`}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          estimatedItemSize={450}
          numColumns={1}
          onEndReached={onEndReached}
          ListFooterComponent={() =>
            onEndReachedSpinner ? (
              <View style={{ flex: 1, padding: 10, alignItems: 'center' }}>
                <Spinner size="large" color="$green10" />
              </View>
            ) : null
          }
        />
      </XStack>

      <XStack marginTop={20} alignItems="center" paddingHorizontal={10}>
        <Avatar circular size="$3" marginTop={10}>
          <Avatar.Image
            accessibilityLabel="Nate Wienert"
            src={authorImage ? authorImage : FallBackImage}
          />
          <Avatar.Fallback delayMs={600} />
        </Avatar>

        <YStack
          flex={1}
          backgroundColor={'$commentSectionBackgroundColor'}
          position="relative"
          marginLeft={10}
          borderRadius={10}
          padding={10}
        >
          <Paragraph
            fontSize={14}
            color={'$commentSectionTextColor'}
            fontWeight={'600'}
            marginBottom={10}
            numberOfLines={1} ellipsizeMode="tail"
          >
            {userProfile?.user?.name}
          </Paragraph>

          <TextArea
            style={{ borderColor: 'transparent' }}
            placeholder={t('page.typeYourCommentHere')}
            ref={textAreaCommentRef}
            onChangeText={(text) => setComment(text)}
            placeholderTextColor="$grey"
            backgroundColor="transparent"
            color={'$commentSectionTextColor'}
            paddingVertical={1}
            paddingRight={30}
            paddingLeft={1}
            fontSize={14}
            height={45}
            width="100%"
            onPress={onCommentInputPress}
            testID={`kisan_tId_commentInput_${index}`}
          />

          <Stack
            position="absolute"
            bottom={20}
            right={10}
            borderRadius={50}
            borderWidth={1}
            borderColor={'rgba(115, 115, 115, 0.40)'}
            backgroundColor={
              comment.trim().length !== 0 && !isReplySubmitting ? '$primary' : '$grey'
            }
            padding={8}
            width={30}
            height={30}
            justifyContent="center"
            alignItems="center"
            onPress={handleAddComment}
            testID={`kisan_tId_commentSend_${index}`}
            style={{ opacity: isReplySubmitting || comment.trim().length === 0 ? 0.5 : 1 }}
          >
            <SendHorizontal color="#FFF" size={18} />
          </Stack>
        </YStack>
      </XStack>
    </>
  );
}
